import { IonCard, IonImg } from "@ionic/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserTrickSearch } from "../../../models/user-trick-search";
import UsernameDisplay from "../../ComponentsUI/Username/UsernameDisplay";

interface ContainerProps {
    trickArray: UserTrickSearch[];
    onTrickSelection: (trick: UserTrickSearch) => void;
    loggedInId?: string;
    trickSearch: string;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

// Fixed row height to prevent layout shifts during scroll
const ESTIMATED_ROW_HEIGHT = 250;

const TrickSearchCardHolder: React.FC<ContainerProps> = (props: ContainerProps) => {
    const [columnCount, setColumnCount] = useState(4);
    const parentRef = useRef<HTMLDivElement>(null);
    const loadMoreTriggeredRef = useRef(false);

    const [contentHeight, setContentHeight] = useState('100%');

    // Add resize observer to handle height changes
    useEffect(() => {
        const ionContent = parentRef.current?.closest('ion-content');
        if (!ionContent) return;

        const resizeObserver = new ResizeObserver(() => {
            setContentHeight(`${ionContent.offsetHeight}px`);
        });

        resizeObserver.observe(ionContent);

        // Initial height
        setContentHeight(`${ionContent.offsetHeight}px`);

        return () => resizeObserver.disconnect();
    }, []);

    // Handle responsive column count
    useEffect(() => {
        const updateColumnCount = () => {
            const width = window.innerWidth;
            if (width >= 1280) setColumnCount(6);      // xl:grid-cols-6
            else if (width >= 768) setColumnCount(4);  // md:grid-cols-4
            else if (width >= 640) setColumnCount(3);  // sm:grid-cols-3
            else if (width >= 480) setColumnCount(2);  // xs:grid-cols-2
            else setColumnCount(2);                    // default
        };

        updateColumnCount();
        window.addEventListener('resize', updateColumnCount);
        return () => window.removeEventListener('resize', updateColumnCount);
    }, []);

    // Memoize row count to prevent unnecessary recalculations
    const rowCount = useMemo(() =>
        Math.ceil(props.trickArray.length / columnCount),
        [props.trickArray.length, columnCount]
    );

    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => parentRef.current,
        estimateSize: useCallback(() => ESTIMATED_ROW_HEIGHT, []),
        // Increase overscan significantly for smoother reverse scrolling
        overscan: 5,
    });

    // Memoize trick selection handler
    const handleTrickClick = useCallback((trick: UserTrickSearch) => {
        props.onTrickSelection(trick);
    }, [props.onTrickSelection]);

    // Detect when user scrolls near bottom to trigger load more
    useEffect(() => {
        const scrollElement = parentRef.current;
        if (!scrollElement || !props.onLoadMore) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollElement;
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

            // Trigger load more when scrolled past 80% and not already loading
            if (scrollPercentage > 0.8 && !loadMoreTriggeredRef.current && !props.isLoadingMore) {
                loadMoreTriggeredRef.current = true;
                props.onLoadMore();
            }
        };

        scrollElement.addEventListener('scroll', handleScroll);
        return () => scrollElement.removeEventListener('scroll', handleScroll);
    }, [props.onLoadMore, props.isLoadingMore]);

    // Reset load more trigger when new data arrives
    useEffect(() => {
        if (!props.isLoadingMore) {
            loadMoreTriggeredRef.current = false;
        }
    }, [props.trickArray.length, props.isLoadingMore]);

    // Auto-load more if content doesn't fill viewport (iPad/larger screens issue)
    // This handles the case where initial content fits without scrolling
    useEffect(() => {
        const scrollElement = parentRef.current;
        if (!scrollElement || !props.onLoadMore || props.isLoadingMore) return;

        // Check after a short delay to allow layout to settle
        const checkContentFit = () => {
            const { scrollHeight, clientHeight } = scrollElement;

            // If content doesn't require scrolling and we have items, load more
            // scrollHeight <= clientHeight means no scrollbar/can't scroll
            if (scrollHeight <= clientHeight && props.trickArray.length > 0 && !loadMoreTriggeredRef.current) {
                console.log('Content fits in viewport without scrolling, auto-loading more');
                loadMoreTriggeredRef.current = true;
                props.onLoadMore();
            }
        };

        // Small delay to let the virtualizer measure content
        const timeoutId = setTimeout(checkContentFit, 100);
        return () => clearTimeout(timeoutId);
    }, [props.trickArray.length, props.onLoadMore, props.isLoadingMore, columnCount, contentHeight]);

    return (
        <div
            ref={parentRef}
            className="h-full w-full overflow-auto"
            style={{
                position: 'relative',
                width: '100%',
                height: contentHeight,
                minHeight: contentHeight,
                // Add hardware acceleration for smoother scrolling
                willChange: 'scroll-position',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                    <div
                        key={virtualRow.key}
                        style={{
                            position: 'absolute',
                            top: virtualRow.start,
                            left: 0,
                            width: '100%',
                            height: ESTIMATED_ROW_HEIGHT,
                            display: 'flex',
                            gap: '0.5rem',     // gap-2
                            padding: '0.25rem' // p-1
                        }}
                    >
                        {/* Render all columns directly without column virtualizer */}
                        {Array.from({ length: columnCount }).map((_, colIndex) => {
                            const trickIndex = virtualRow.index * columnCount + colIndex;
                            const trick = props.trickArray[trickIndex];
                            if (!trick) return null;

                            return (
                                <div
                                    key={`${virtualRow.index}-${colIndex}`}
                                    style={{
                                        width: `${100 / columnCount}%`,
                                        padding: '8px',
                                    }}
                                    onClick={() => handleTrickClick(trick)}
                                >
                                    <IonCard className="cursor-pointer p-0 m-0 border border-gray-900 bg-black font-bold">
                                        <IonImg
                                            className="object-cover min-w-full min-h-full h-full aspect-video"
                                            src={trick?.thumbnail}
                                        />
                                        <div className="bg-black p-4">
                                            <div className="text-base text-white">{trick.trick_name}</div>
                                            <div className="text-sm">
                                                <UsernameDisplay
                                                    username={trick.username}
                                                    className=""
                                                    loggedInId={props.loggedInId}
                                                    userId={trick.user_id}
                                                />
                                            </div>
                                        </div>
                                    </IonCard>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TrickSearchCardHolder;